import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  COLOR,
  type ColorMode,
  fieldBorderRadius,
  fieldDisabledOpacity,
  fieldErrorShadow,
  fieldFocusShadow,
  fieldLabelFontSize,
  fieldLabelMargin,
  FONT_SIZE,
  INPUT_COLORS,
  SIZE,
} from '../../styles';
import { a11yKey } from '../../utils/ComponentUtil';
import { FieldInfo } from '../FieldInfo';
import { Grid } from '../Grid';
import { KeyActions } from '../KeyActions';
import { Remove } from '../SVG';
import { type InputProps } from './Input.types';

let nextTextFieldId = 0;
const RawInput: React.FC<InputProps & { forwardedRef?: React.Ref<HTMLInputElement> }> = memo(
  (props) => {
    const {
      colorMode = 'light',
      errorText,
      hintText,
      warningText,
      inputStyle,
      labelText,
      inline,
      onClear,
      forwardedRef,
      onFocus,
      className,
      style,
      showLabelAsterisk,
      inputComponent,
      ...inputProps
    } = props;

    const name = useMemo(() => {
      let name = props.name;
      if (props.labelText && !props.name) {
        // Need a `name` here, because we need to create an `htmlFor` on the label.
        // Generate a unique one.
        name = `text-field-${nextTextFieldId++}`;
      }
      return name;
    }, [props.name, props.labelText]);

    const inputRef = useRef<HTMLInputElement | null>(null);
    const [focus, setFocus] = useState(false);
    const [hover, setHover] = useState(false);
    const [shortcuts] = useState([
      {
        shortcut: 'esc',
        action: () => {
          if (onClear && focus) {
            handleBlurClear();
          }
        },
        isFormSafe: true,
      },
    ]);

    const addFocus = () => setFocus(true);
    const removeFocus = () => setFocus(false);
    const addHover = () => setHover(true);
    const removeHover = () => setHover(false);

    const handleFocus = (focusEvent: React.FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
        onFocus(focusEvent);
      }

      if (inputRef.current) {
        inputRef.current.select();
      }
    };

    const handleBlurClear = () => {
      if (isEmpty(props.value)) {
        inputRef.current?.blur();
        setFocus(false);
        setHover(false);
      } else if (onClear) {
        // This cannot go into defaultProps, because `onClear` needs to be nil sometimes
        onClear();
        inputRef.current?.focus();
      }
    };

    const setupReference = useCallback(
      (node: HTMLInputElement) => {
        inputRef.current = node;

        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          (forwardedRef as any).current = node;
        }
      },
      [forwardedRef],
    );

    const fieldsetClass = classNames(`mode-${colorMode}`, className, {
      disabled: props.disabled,
      inline,
    });

    const inputClass = classNames(`mode-${colorMode}`, {
      active: focus || hover,
      disabled: props.disabled,
      error: errorText,
    });

    const labelClass = classNames(`mode-${colorMode}`, {
      active: focus || hover,
      disabled: props.disabled,
      error: errorText,
      'label-asterisk': showLabelAsterisk,
    });

    const isVisible = !!onClear && !!props.value && !props.disabled;

    const clearButtonClass = classNames('clear-button', `mode-${colorMode}`, {
      labelled: props.labelText,
      visible: isVisible,
    });

    let typePattern = props.pattern;

    if (!typePattern && props.type === 'tel') {
      typePattern = '[0-9]{3} [0-9]{3} [0-9]{4}';
    }

    return (
      <KeyActions actions={shortcuts}>
        {/* TODO: Shouldn't wrap each individual input in a fieldset.  This is not very semantic-web friendly. */}
        <Fieldset
          className={fieldsetClass}
          onFocus={addFocus}
          onBlur={removeFocus}
          onMouseOver={addHover}
          onMouseOut={removeHover}
          style={style}
        >
          <Grid
            gridTemplateColumns={inline ? 'auto 1fr' : '100%'}
            gridTemplateRows="auto"
            gridColumnGap={inline ? '22px' : '0'}
          >
            {labelText && (
              <Label
                className={labelClass}
                htmlFor={name}
                data-testid={`${props['data-testid'] || 'input'}-label`}
              >
                {labelText}
              </Label>
            )}
            <InputWrapper className="input-wrapper">
              <BareInput
                as={inputComponent}
                colorMode={colorMode}
                {...inputProps}
                type={props.type || 'text'}
                id={name}
                name={name}
                className={inputClass}
                onFocus={handleFocus}
                ref={setupReference}
                style={inputStyle}
                data-testid={`${props['data-testid'] || 'input'}-input`}
                isClearButtonVisible={isVisible}
              />
              <ClearButton
                className={clearButtonClass}
                onClick={handleBlurClear}
                onKeyDown={a11yKey(handleBlurClear)}
                data-testid={`${props['data-testid'] || 'input'}-clear-button`}
              >
                <Remove />
              </ClearButton>
            </InputWrapper>
            <UnderInputWrapper className="under-input-wrapper">
              {!errorText && !warningText && hintText && (
                <FieldInfo colorMode={colorMode} text={hintText} />
              )}
              {!errorText && warningText && (
                <FieldInfo colorMode={colorMode} type="warning" text={warningText} />
              )}
              {errorText && <FieldInfo colorMode={colorMode} type="error" text={errorText} />}
            </UnderInputWrapper>
          </Grid>
        </Fieldset>
      </KeyActions>
    );
  },
);

// Styled components
const Fieldset = styled.fieldset`
  border: none;
  display: block;
  font-family: inherit;
  margin: 0 0 ${SIZE['20']};
  padding: 0;
  position: relative;
  width: 100%;

  fieldset + &:last-child {
    margin-bottom: 0;
  }

  &,
  label,
  input {
    box-sizing: border-box;
    display: block;
    width: 100%;
  }

  &.disabled {
    opacity: ${fieldDisabledOpacity};

    &,
    input:disabled,
    label {
      cursor: not-allowed;
    }
  }

  &.inline {
    label {
      width: auto;
      grid-row-start: 1;
    }

    .input-wrapper {
      grid-row-start: 1;
    }

    .input-wrapper,
    .under-input-wrapper {
      grid-column-start: 2;
    }
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const UnderInputWrapper = styled.div`
  width: 100%;

  .field-legend {
    margin-top: 2px;
  }
`;

export function bareInputStyle(colorMode: ColorMode = 'light'): string {
  return `
  background: ${INPUT_COLORS[colorMode].background};

  // Sets the autofill color, prevents chrome from having a light mode background color when autocompleting the dark mode input.
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-text-fill-color: ${INPUT_COLORS[colorMode].color};
    -webkit-box-shadow: 0 0 0 50px ${INPUT_COLORS[colorMode].backgroundDisabled} inset !important;
  }

  border-radius: ${fieldBorderRadius};
  border: 1px solid ${INPUT_COLORS[colorMode].borderColor};
  color: ${INPUT_COLORS[colorMode].color};
  font-size: ${FONT_SIZE['14']};
  line-height: 18px;

  :disabled {
    background: ${INPUT_COLORS[colorMode].backgroundDisabled};
    border-color: ${INPUT_COLORS[colorMode].borderColorDisabled};
  }

  + input-placeholder {
    color: ${COLOR['light-gray']};
    opacity: ${fieldDisabledOpacity};
  }

  &::placeholder {
    color: ${COLOR['light-gray-darkest']};
  }

  &.error,
  &:invalid {
    &:not(.active),
    &:not(:focus),
    &:not(:hover) {
      border-color: ${INPUT_COLORS[colorMode].borderColorError};
      box-shadow: ${fieldErrorShadow};
    }
  }

  &:not(:disabled) {
    &.active,
    &:focus,
    &:hover {
      box-shadow: ${fieldFocusShadow};
      outline: none;
      border: 1px solid ${COLOR['primary']};
    }
  }
`;
}

const BareInput = styled('input').withConfig({
  shouldForwardProp: (prop) =>
    !['refFunc', 'colorMode', 'dataTestId', 'isClearButtonVisible'].includes(prop),
})<{ colorMode: ColorMode | undefined; isClearButtonVisible?: boolean }>`
  ${(props) => bareInputStyle(props.colorMode)}
  padding: ${(props) => (props.isClearButtonVisible ? '7px 34px 7px 12px' : '7px 12px')};
`;

const Label = styled.label`
  color: ${COLOR['fieldLabelColor']};
  cursor: pointer;
  font-size: ${fieldLabelFontSize};
  line-height: ${SIZE['16']};
  margin: ${fieldLabelMargin};
  padding: 0;

  &.error {
    color: ${COLOR['red-dark']};
  }

  &.mode-darker,
  &.mode-dark {
    color: ${COLOR['white']};

    &.error {
      color: ${COLOR['red']};
    }

    &.active:not(.disabled) {
      color: ${COLOR['turquoise']};
    }
  }

  &.disabled {
    cursor: not-allowed;
  }

  &.active:not(.disabled) {
    color: ${COLOR['primary']};
  }

  &.label-asterisk::after {
    margin-left: 2px;
    content: '*';
    color: var(--color-red);
  }
`;

const ClearButton = styled.a`
  display: none;
  border-radius: 0 ${fieldBorderRadius} ${fieldBorderRadius} 0;
  cursor: pointer;
  height: 34px;
  padding: 11px;
  position: absolute;
  right: 0;
  top: 0;

  &.visible {
    display: block;
  }

  &:focus,
  &:hover {
    background: ${COLOR['primary']};
    svg {
      polygon,
      path {
        fill: ${COLOR['white']};
      }
    }
  }

  svg {
    transform: translate(0px, -1.5px);

    polygon,
    path {
      fill: ${COLOR['primary']};
    }
  }

  &.mode-darker,
  &.mode-dark {
    svg {
      polygon,
      path {
        fill: ${COLOR['white']};
      }
    }

    &:focus,
    &:hover {
      background: ${COLOR['turquoise']};
    }
  }
`;

export const Input = React.forwardRef((props: InputProps, ref: React.Ref<HTMLInputElement>) => (
  <RawInput {...props} forwardedRef={ref} />
));

export default Input;
RawInput.displayName = 'RawInput';
Input.displayName = 'Input';

export { BareInput };
